/**
 * Arthaus Email API client — the artist's own inbox (no ids for inbox or artist; the session is
 * the identity).
 */

import { apiClient } from '@/lib/api/client'
import type { Availability, Folder, Inbox, InboxCounts, InboxState, InboxView, OutgoingMail, ThreadDetail, ThreadSummary } from '../types'

const BASE = '/api/v1/easel/inbox'

interface Envelope<T> {
  success: boolean
  data: T
}

/** Mail goes as multipart so attachments can ride along; the server reads the same fields from JSON. */
function mailForm(mail: OutgoingMail): FormData {
  const form = new FormData()
  const fields: (keyof OutgoingMail)[] = ['to', 'cc', 'bcc', 'subject', 'text', 'messageId']
  fields.forEach((k) => {
    const v = mail[k]
    if (v !== undefined && v !== null && v !== '') form.append(k, String(v))
  })
  if (mail.replyAll) form.append('replyAll', 'true')
  ;(mail.files || []).forEach((f) => form.append('attachments', f, f.name))
  return form
}

const multipart = { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }

export const inboxApi = {
  getState: async (): Promise<InboxState> =>
    (await apiClient.get<Envelope<InboxState>>(BASE)).data.data,

  checkAvailability: async (name: string): Promise<Availability> =>
    (await apiClient.get<Envelope<Availability>>(`${BASE}/availability`, { params: { name } })).data.data,

  claim: async (body: { localPart: string; fromName?: string }): Promise<Inbox> =>
    (await apiClient.post<Envelope<Inbox>>(BASE, body)).data.data,

  updateSettings: async (body: Partial<Pick<Inbox, 'fromName' | 'notifyPersonal' | 'autoDraft' | 'signature'>>): Promise<Inbox> =>
    (await apiClient.patch<Envelope<Inbox>>(BASE, body)).data.data,

  sync: async (): Promise<{ synced: number; triaged: number; counts: InboxCounts }> =>
    (await apiClient.post<Envelope<{ synced: number; triaged: number; counts: InboxCounts }>>(`${BASE}/sync`, {})).data.data,

  listThreads: async (view: InboxView, q?: string): Promise<{ data: ThreadSummary[]; total: number }> => {
    const res = await apiClient.get<Envelope<ThreadSummary[]> & { pagination: { total: number } }>(
      `${BASE}/threads`,
      { params: { view, q: q || undefined, limit: 100 } }
    )
    return { data: res.data.data, total: res.data.pagination.total }
  },

  getThread: async (id: number): Promise<ThreadDetail> =>
    (await apiClient.get<Envelope<ThreadDetail>>(`${BASE}/threads/${id}`)).data.data,

  compose: async (mail: OutgoingMail) =>
    (await apiClient.post<Envelope<{ id: string; thread: ThreadSummary | null }>>(`${BASE}/compose`, mailForm(mail), multipart)).data.data,

  reply: async (id: number, mail: OutgoingMail) =>
    (await apiClient.post<Envelope<{ id: string; thread: ThreadSummary }>>(`${BASE}/threads/${id}/reply`, mailForm(mail), multipart)).data.data,

  forward: async (id: number, mail: OutgoingMail) =>
    (await apiClient.post<Envelope<{ id: string }>>(`${BASE}/threads/${id}/forward`, mailForm(mail), multipart)).data.data,

  attachmentLink: async (threadId: number, messageId: number, attachmentId: string) =>
    (await apiClient.get<Envelope<{ url: string; filename: string | null }>>(
      `${BASE}/threads/${threadId}/messages/${messageId}/attachments/${attachmentId}`
    )).data.data,

  markRead: async (id: number, read: boolean) =>
    (await apiClient.patch<Envelope<ThreadSummary>>(`${BASE}/threads/${id}`, { read })).data.data,

  saveDraft: async (id: number, text: string) =>
    (await apiClient.put<Envelope<{ draftText: string | null }>>(`${BASE}/threads/${id}/draft`, { text })).data.data,

  retriage: async (id: number): Promise<ThreadDetail> =>
    (await apiClient.post<Envelope<ThreadDetail>>(`${BASE}/threads/${id}/triage`, {})).data.data,

  move: async (id: number, folder: Folder) =>
    (await apiClient.patch<Envelope<ThreadSummary>>(`${BASE}/threads/${id}`, { folder })).data.data,

  convertToCommission: async (id: number, title?: string) =>
    (await apiClient.post<Envelope<{ commission: { id: number; reference: string; title: string }; thread: ThreadSummary; commissionsVisible: boolean }>>(
      `${BASE}/threads/${id}/commission`,
      { title }
    )).data.data,
}
